import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, Facebook, Instagram, Youtube, Twitter, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { LOGO_URL, CATEGORIES } from "@/data/constants";
import SearchDialog from "@/components/SearchDialog";

const NavLink = ({ to, children }) => (
  <Link
    to={to}
    className="overline text-[#0A192F] hover:text-[#8B6D1F] transition-colors py-2"
  >
    {children}
  </Link>
);

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const socials = [
    { Icon: Facebook, label: "Facebook" },
    { Icon: Instagram, label: "Instagram" },
    { Icon: Youtube, label: "YouTube" },
    { Icon: Twitter, label: "X" },
  ];

  return (
    <header className="sticky top-0 z-50">
      {/* Utility bar */}
      <div className="bg-[#0A192F] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between">
          <span className="hidden sm:block text-white/70 tracking-wide">
            The World's Most Trusted Source for Wolfdog News &amp; Information
          </span>
          <div className="flex items-center gap-5 ml-auto">
            <Link to="/about" className="hover:text-[#D4AF37] transition-colors">About Us</Link>
            <Link to="/contact" className="hover:text-[#D4AF37] transition-colors">Contact</Link>
            <Link to="/#newsletter" className="hover:text-[#D4AF37] transition-colors">Subscribe</Link>
            <div className="hidden md:flex items-center gap-3 pl-3 border-l border-white/20">
              {socials.map(({ Icon, label }) => (
                <a key={label} href="#" aria-label={label} className="text-white/70 hover:text-[#D4AF37] transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white/95 backdrop-blur border-b border-[#0A192F]/10">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-28">
          <Link to="/" data-testid="logo-home-link" className="flex items-center">
            <img src={LOGO_URL} alt="Wolfdog News" className="h-24 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            <DropdownMenu>
              <DropdownMenuTrigger className="overline text-[#0A192F] hover:text-[#8B6D1F] transition-colors flex items-center gap-1 outline-none">
                News <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white">
                <DropdownMenuItem onClick={() => navigate("/news")} className="cursor-pointer">All News</DropdownMenuItem>
                {CATEGORIES.map((c) => (
                  <DropdownMenuItem key={c} onClick={() => navigate(`/news?category=${c}`)} className="cursor-pointer">
                    {c}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <NavLink to="/legislation">Legislation</NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger className="overline text-[#0A192F] hover:text-[#8B6D1F] transition-colors flex items-center gap-1 outline-none">
                Articles <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white">
                <DropdownMenuItem onClick={() => navigate("/articles")} className="cursor-pointer">Guest Articles</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/articles#submit")} className="cursor-pointer">Submit an Article</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <NavLink to="/information">Information</NavLink>
            <NavLink to="/events">Events</NavLink>
            <NavLink to="/resources">Resources</NavLink>
            <NavLink to="/newsletter">Newsletter</NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              data-testid="search-open-button"
              className="p-2 text-[#0A192F] hover:text-[#8B6D1F] transition-colors"
            >
              <Search className="w-5 h-5" />
            </button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 text-[#0A192F]" aria-label="Menu" data-testid="mobile-menu-button">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white w-72">
                <div className="flex flex-col gap-1 mt-8 text-base">
                  {[
                    ["Home", "/"],
                    ["All News", "/news"],
                    ["Legislation Center", "/legislation"],
                    ["Guest Articles", "/articles"],
                    ["Information Hub", "/information"],
                    ["Events", "/events"],
                    ["Resources", "/resources"],
                    ["Newsletter", "/newsletter"],
                    ["About", "/about"],
                    ["Contact", "/contact"],
                  ].map(([label, to]) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMobileOpen(false)}
                      className="py-3 border-b border-[#0A192F]/10 font-medium text-[#0A192F] hover:text-[#8B6D1F]"
                      data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};

export default Header;
